<!-- eslint-disable vue/no-v-html -->
<template>
  <div ref="root" class="hero-legacy">
    <div ref="wrapper" class="hero-legacy-inner wrapper">
      <div class="hero-legacy__headline">
        <h1 v-html="content.headline"></h1>
        <div class="hero-legacy__scroller-container">
          <div class="hero-legacy__scroller"></div>
        </div>
      </div>

      <div ref="items" class="hero-legacy__title">
        <div v-html="content.title[0]"></div>
        <div>
          <span v-html="content.title[1]"></span>
          <span
            v-for="index in 8"
            :key="`swap-word-${index}`"
            class="hero-legacy__title__swap-word"
            v-html="content.title[1]"
          ></span>
        </div>
        <div>
          <span v-html="content.title[2]"></span>
          <GPicture
            :content="content.picture"
            class="hero-legacy__picture cover"
            sizes="20vw"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { Picture } from '@/types'

export interface HeroLegacy {
  headline: string
  title: string[]
  picture: Picture
}
</script>

<script setup lang="ts">
import gsap from 'gsap'
import { onMounted, ref } from 'vue'

import { useAppear } from '@/utils/aware'

import type { PropType } from 'vue'

const root = ref(null)
const wrapper = ref()
const items = ref()

defineProps({
  content: {
    type: Object as PropType<HeroLegacy>,
    required: true,
  },
})

onMounted(() => {
  if (root.value) {
    const tl = gsap.timeline({})
    tl.set(wrapper.value, {
      opacity: 0,
      y: 60,
    })
  }
})

useAppear(
  root,
  ({ isInViewport }) => {
    if (isInViewport) {
      const tl = gsap.timeline()

      tl.to(wrapper.value, {
        duration: 1.2,
        opacity: 1,
        y: 0,
        ease: 'Power1.easeOut',
      })

      if (items.value.children.length > 0) {
        tl.fromTo(
          items.value.children,
          {
            opacity: 0,
            y: 75,
          },
          {
            opacity: 1,
            y: 0,
            stagger: 0.4,
            duration: 0.4,
            ease: 'Power4.inOut',
          },
          '-=0.2'
        )
      }

      const cards = document.querySelectorAll('.hero-legacy__title__swap-word')
      cards?.forEach((card, index) => {
        tl.fromTo(
          card,
          {
            opacity: 0,
            y: 75,
          },
          {
            opacity: 1,
            y: 0,
            stagger: 1,
            duration: (cards.length - index) * 0.05,
            ease: 'Power4.inOut',
          },
          '+=.2'
        )
      })
    }
  },
  { rootMargin: '-25% 0px', once: true }
)
</script>

<style lang="scss" scoped>
.hero-legacy {
  position: relative;
  background-color: $c-green-dark;
}

.hero-legacy-inner {
  height: 100%;
}

.hero-legacy__headline {
  @extend %fraunces-spec;
  @extend %fw-light;

  // prettier-ignore
  @include fluid(font-size, (xxs: 3.5rem, xxl: 10rem));

  margin-top: 0;
  color: $c-white;
  line-height: 1;
  text-align: center;

  @include mq($until: s) {
    margin-bottom: 5rem;
    padding-top: 15rem;
  }

  @include mq(s) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    gap: $spacing * 4;
  }
}

.hero-legacy__title {
  @extend %fraunces-spec;

  position: relative;
  z-index: 1;
  padding-block: $spacing;
  color: $c-white;
  font-size: min(18rem, 12.5cqw);
  font-weight: 300;
  line-height: 1;
  text-transform: uppercase;
  container: inline-size;

  > div {
    color: $c-green-light;

    :deep(strong) {
      font-weight: 300;
    }

    &:nth-child(2) {
      position: relative;
      justify-content: center;
      color: $c-white;

      .hero-legacy__title__swap-word {
        position: absolute;
        top: 0;
        left: 0;
        background-color: $c-green-dark;

        /* stylelint-disable-next-line max-nesting-depth */
        @include mq(s) {
          left: 50%;
          transform: translateX(-50%);
        }
      }
    }

    @include mq($until: s) {
      span {
        display: block;

        /* stylelint-disable-next-line max-nesting-depth */
        &:nth-child(2) {
          text-align: right;
        }
      }
    }

    @include mq(s) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  @include mq($until: s) {
    padding-bottom: 5rem;
  }

  @include mq(s) {
    min-height: 100vh;
  }
}

.hero-legacy__picture {
  // prettier-ignore
  @include fluid(width, (xxs: 6rem, xxl: 16rem));

  // prettier-ignore
  @include fluid(border-radius, (xxs: .45rem, xxl: 1.2rem));

  position: relative;
  display: block;
  overflow: hidden;
  aspect-ratio: 1;

  @include mq($until: s) {
    margin-top: 0.8rem;
    margin-inline: auto;
  }
}

.hero-legacy__scroller-container {
  position: relative;
  width: 3rem;
  height: 6rem;
  border: 3px solid $c-white;
  border-radius: 1.5rem;
}

.hero-legacy__scroller {
  position: absolute;
  top: 0.4rem;
  bottom: 3.4rem;
  left: 0.4rem;
  width: 1.6rem;
  background-color: $c-white;
  border-radius: 0.8rem;
  animation: scroller 1500ms ease-out infinite;
}

@keyframes scroller {
  0% {
    bottom: 3.4rem;
  }

  5% {
    top: 0.4rem;
  }

  32% {
    bottom: 0.4rem;
  }

  66% {
    top: 3.4rem;
    bottom: 0.4rem;
  }

  100% {
    top: 0.4rem;
    bottom: 3.4rem;
  }
}
</style>
